/**
 * Organisation → Security notice addresses (migration 093): up to 3 addresses
 * outside the team (security@, say) that get the AI agent and security
 * notices. Owners and admins. An address gets nothing until it confirms from
 * the email we send it; each notice has a link that removes it. They never get
 * lead or homeowner details. Every change is on the audit log. AI agents can't
 * see or change this list.
 */
import { useState, type FormEvent } from 'react';
import { useSession } from '../auth/session';
import { can } from '../auth/roles';
import { ErrorNotice, Field, formatDateTime, useToast } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function NoticeRecipients() {
  const { api, org, role } = useSession();
  const toast = useToast();
  const allowed = can(role, 'editOrg');
  const list = useQuery(() => api.listNoticeRecipients(), ['notice-recipients', org?.id], { enabled: allowed });
  const [email, setEmail] = useState('');
  const add = useMutation((address: string) => api.addNoticeRecipient(address));
  const resend = useMutation((id: string) => api.resendNoticeRecipient(id));
  const remove = useMutation(async (id: string) => {
    await api.removeNoticeRecipient(id);
    return true;
  });
  if (!allowed) return null;
  const items = list.data?.items ?? [];
  const max = list.data?.max ?? 3;
  const cleaned = email.trim().toLowerCase();
  const full = items.length >= max;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const res = await add.run(cleaned);
    if (res) {
      setEmail('');
      await list.reload();
      toast(res.confirmation_sent === false ? 'Added, but the confirmation email failed. Try Resend.' : `Confirmation sent to ${res.email}`);
    }
  };

  return (
    <section className="panel panel-pad stack-sm" aria-labelledby="notice-recipients-h">
      <h2 id="notice-recipients-h" className="h-sub">Security notice addresses</h2>
      <p className="small muted" style={{ margin: 0 }}>
        Up to {max} addresses outside your team, such as security@, that get the AI agent and security notices. Each must confirm
        from the email we send before it gets anything, and can stop them from any notice. They never get lead or homeowner details.
      </p>
      <ErrorNotice error={list.error ?? resend.error ?? remove.error} />
      {items.length ? (
        <ul className="stack-sm" style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label="Security notice addresses">
          {items.map((r) => (
            <li key={r.id} className="row-between" style={{ gap: 8, flexWrap: 'wrap' }}>
              <span style={{ overflowWrap: 'anywhere' }}>
                {r.email}{' '}
                <span className={`tag ${r.status === 'confirmed' ? 'ok' : 'warn'}`}>{r.status === 'confirmed' ? 'Confirmed' : 'Waiting to confirm'}</span>
                {r.status === 'pending' && r.confirm_expires_at ? (
                  <span className="small muted"> Link expires {formatDateTime(r.confirm_expires_at)}.</span>
                ) : null}
              </span>
              <span className="row" style={{ gap: 6 }}>
                {r.status === 'pending' ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={resend.pending}
                    aria-label={`Resend confirmation to ${r.email}`}
                    onClick={async () => {
                      const res = await resend.run(r.id);
                      if (res) {
                        await list.reload();
                        toast(`New confirmation sent to ${r.email}`);
                      }
                    }}
                  >
                    Resend
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={remove.pending}
                  aria-label={`Remove ${r.email}`}
                  onClick={async () => {
                    const res = await remove.run(r.id);
                    if (res) {
                      await list.reload();
                      toast(`${r.email} removed`);
                    }
                  }}
                >
                  Remove
                </button>
              </span>
            </li>
          ))}
        </ul>
      ) : list.data ? (
        <p className="small" style={{ margin: 0 }}>No addresses yet. Owners and admins get these notices already.</p>
      ) : null}
      {full ? (
        <p className="small muted" style={{ margin: 0 }}>You have {max} addresses. Remove one to add another.</p>
      ) : (
        <form className="stack-sm" onSubmit={submit} noValidate aria-label="Add a security notice address">
          <Field label="Add an address">
            {(p) => (
              <input
                {...p}
                className="input"
                type="email"
                autoComplete="off"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="security@company.com"
              />
            )}
          </Field>
          <ErrorNotice error={add.error} />
          <div>
            <button className="btn btn-sm" type="submit" disabled={add.pending || !EMAIL_SHAPE.test(cleaned)}>
              {add.pending ? 'Sending…' : 'Send confirmation'}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
